import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicPage, PageHero, Seo, PLANS } from "@/components/public/PublicChrome";
import { trackAcq, lastTool } from "@/lib/acquisition";

const FAQ = [
  ["Is there really a free trial?", "Yes — every plan includes a 7-day free trial with no credit card required."],
  ["Can I change plans later?", "Absolutely. Upgrade or downgrade anytime from your billing settings."],
  ["What happens when my trial ends?", "You'll be prompted to subscribe. Your data, documents, and progress are always preserved."],
  ["Do you offer annual billing?", "Yes. Switch to annual to save the equivalent of two months versus paying monthly."],
];

export default function Pricing() {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");
  const annual = cycle === "annual";
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackAcq("pricing_view", { landing: "/pricing", tool: lastTool() });
  }, []);
  const selectPlan = (name) => {
    trackAcq("plan_selected", { plan_id: name.toLowerCase(), billing_cycle: cycle, tool: lastTool() });
    navigate(`/register?plan=${name.toLowerCase()}&cycle=${cycle}`);
  };
  return (
    <PublicPage>
      <Seo title="Pricing" path="/pricing" description="Simple, transparent pricing. Start any Fairview Systems plan with a 7-day free trial — no credit card required. Monthly or annual billing." />
      <PageHero eyebrow="Pricing" title="Simple, transparent pricing"
        subtitle="Start any plan with a 7-day free trial. No credit card required. Cancel anytime." />
      <section className="py-20 px-6">
        {/* Choose-by-stage supporting copy */}
        <p className="max-w-2xl mx-auto text-center text-sm text-[#A1A1AA] mb-10" data-testid="pricing-stage-help">
          Pick the plan that matches where your business is <span className="text-white">today</span> — and move up a
          tier whenever you're ready. Every plan starts with a free 7-day trial, and your data always comes with you.
        </p>
        {/* Monthly / Annual selector */}
        <div className="flex items-center justify-center gap-3 mb-12" data-testid="pricing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="pricing-cycle-monthly"
            className={`px-5 py-2 rounded-full text-sm transition-colors ${!annual ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>Monthly</button>
          <button onClick={() => setCycle("annual")} data-testid="pricing-cycle-annual"
            className={`px-5 py-2 rounded-full text-sm transition-colors ${annual ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>
            Annual <span className={`ml-1 text-[11px] ${annual ? "text-[#0A0A0B]/70" : "text-[#D4AF37]"}`}>save 2 months</span>
          </button>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {PLANS.map((p) => {
            const saving = Math.round(p.price * 12 - p.priceAnnual);
            return (
            <div key={p.name} data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative rounded-2xl p-8 border ${p.featured ? "border-[#D4AF37]/40 bg-[#151310]" : "border-white/8 bg-[#121214]"}`}>
              {p.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full fv-gold-gradient text-[#0A0A0B] text-[10px] font-bold tracking-wide uppercase" data-testid="pricing-badge-growth">Recommended</div>}
              <div className="text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase mb-1.5" data-testid={`pricing-stage-${p.name.toLowerCase()}`}>{p.stage}</div>
              <h3 className="font-heading text-2xl mb-1">{p.name}</h3>
              <p className="text-[11px] text-[#A1A1AA] mb-4 leading-relaxed" data-testid={`pricing-bestfor-${p.name.toLowerCase()}`}>{p.bestFor}</p>
              <div className="mb-2">
                <span className="font-heading text-5xl fv-gold-text" data-testid={`pricing-price-${p.name.toLowerCase()}`}>${annual ? p.priceAnnual : p.price}</span>
                <span className="text-[#71717A]">/{annual ? "yr" : "mo"}</span>
              </div>
              <p className="text-[11px] text-[#10B981] mb-3 h-4" data-testid={`pricing-saving-${p.name.toLowerCase()}`}>
                {annual ? `Save $${saving} vs monthly` : `or $${p.priceAnnual}/yr — save $${saving}`}
              </p>
              <p className="text-xs text-[#A1A1AA] mb-4 leading-relaxed" data-testid={`pricing-outcome-${p.name.toLowerCase()}`}>{p.outcome}</p>
              <ul className="space-y-3 mb-4">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              <p className="text-[11px] text-[#71717A] mb-6 pt-3 border-t border-white/8" data-testid={`pricing-limits-${p.name.toLowerCase()}`}>{p.limits}</p>
              <Button onClick={() => selectPlan(p.name)} data-testid={`pricing-trial-${p.name.toLowerCase()}`}
                className={`w-full rounded-full h-11 ${p.featured ? "fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90" : "bg-white/5 border border-white/15 text-white hover:bg-white/10"}`}>
                Start Your Free 7-Day Trial
              </Button>
              <p className="text-center text-[11px] text-[#71717A] mt-3">No credit card required</p>
            </div>
          );})}
        </div>
        {/* What happens after you subscribe */}
        <div className="max-w-4xl mx-auto mt-16 rounded-2xl border border-white/8 bg-[#121214] p-8" data-testid="pricing-after-purchase">
          <h2 className="font-heading text-2xl text-center mb-2">What happens right after you subscribe</h2>
          <p className="text-center text-sm text-[#71717A] mb-8">No waiting, no surprises. Your plan unlocks instantly.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              ["1", "Instant access", "Your plan's modules unlock the moment payment is confirmed by Stripe — no waiting period."],
              ["2", "A recommended first step", "We point you to the highest-leverage next action for your business, so you get value on day one."],
              ["3", "You stay in control", "Upgrade, downgrade, or cancel anytime from your billing page. Cancel and you keep access until the end of your paid period — your data is always preserved."],
            ].map(([n, t, d]) => (
              <div key={n} className="text-center">
                <div className="w-9 h-9 mx-auto rounded-full fv-gold-gradient text-[#0A0A0B] font-bold flex items-center justify-center mb-3">{n}</div>
                <div className="text-white font-medium text-sm mb-1">{t}</div>
                <p className="text-xs text-[#A1A1AA] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-3xl mx-auto mt-16">
          <h2 className="font-heading text-2xl text-center mb-6">Pricing questions</h2>
          <div className="space-y-3">
            {FAQ.map(([q, a]) => (
              <div key={q} className="rounded-xl border border-white/8 bg-[#121214] p-5">
                <div className="text-white font-medium mb-1">{q}</div>
                <p className="text-sm text-[#A1A1AA]">{a}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#71717A] mt-6">See our <button onClick={() => navigate("/legal/refunds")} className="text-[#D4AF37] hover:underline">Refund & Cancellation Policy</button> for full details.</p>
        </div>
      </section>
    </PublicPage>
  );
}
